import React, {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {Empty, Skeleton, Space, Tag, Timeline, Tooltip, Typography} from "antd";
import {
    ClockCircleOutlined,
    EnvironmentOutlined,
    LoginOutlined,
    MessageOutlined,
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {fetchVisitorJourney} from "./chat-api";

const {Text} = Typography;

/**
 * Where a web visitor has been, and where they are right now.
 *
 * <p>Answers the question every operator opens with — "which page are you on?" — before they have to
 * ask it, and shows the path that led there, which is usually what the question is really about: a
 * visitor who read three pricing pages before asking is asking about pricing.
 *
 * <p>Live-updates by polling rather than over STOMP. Page navigation is a once-in-tens-of-seconds
 * event, so a short poll is indistinguishable from a push here, and it keeps this feature entirely
 * off the realtime path that carries messages — where a mistake costs an operator their session.
 * Polling stops when the chat is no longer live.
 */
const VisitorJourney = ({chatId, channel, live = false, compact = false}) => {
    const {t} = useTranslation();
    const isWeb = channel === "WEB";

    const {data, isLoading} = useQuery({
        queryKey: ["visitor-journey", chatId],
        queryFn: () => fetchVisitorJourney(chatId),
        enabled: !!chatId && isWeb,
        // Live chats refresh; a finished conversation's journey can never change again.
        refetchInterval: live ? 10000 : false,
        refetchOnWindowFocus: live,
        staleTime: live ? 0 : 5 * 60 * 1000,
    });

    const entries = useMemo(() => data?.entries || [], [data]);

    if (!isWeb) return null;
    if (isLoading) return <Skeleton active paragraph={{rows: 3}}/>;
    if (!entries.length) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("journey.empty", "مسیری ثبت نشده است")}
            />
        );
    }

    const current = data?.current;
    const startedFrom = data?.startedFrom;
    // Index rather than identity: two views of the same URL are distinct rows, and the one that
    // matters is the last of them.
    const currentIndex = entries.length - 1;
    const startedIndex = startedFrom
        ? entries.findIndex((e) => e.occurredAt === startedFrom.occurredAt && e.url === startedFrom.url)
        : -1;

    const items = entries.map((e, i) => {
        const isCurrent = i === currentIndex;
        const isStart = i === startedIndex;
        return {
            key: `${e.occurredAt}-${i}`,
            color: isCurrent ? "green" : isStart ? "blue" : "gray",
            dot: isCurrent ? <EnvironmentOutlined/> : isStart ? <MessageOutlined/> : undefined,
            children: (
                <div style={{paddingBottom: 2}}>
                    <Space size={6} wrap>
                        <Tooltip title={e.url}>
                            <a
                                href={e.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{fontWeight: isCurrent ? 600 : 400, wordBreak: "break-all"}}
                            >
                                {e.title || e.path || e.url}
                            </a>
                        </Tooltip>
                        {isCurrent && (
                            <Tag color="green" style={{borderRadius: 999, margin: 0}}>
                                {t("journey.current", "همین حالا اینجاست")}
                            </Tag>
                        )}
                        {isStart && !isCurrent && (
                            <Tag color="blue" style={{borderRadius: 999, margin: 0}}>
                                {t("journey.started_here", "چت را از اینجا شروع کرد")}
                            </Tag>
                        )}
                        {e.preChat && (
                            <Tag style={{borderRadius: 999, margin: 0}}>
                                {t("journey.before_chat", "قبل از شروع چت")}
                            </Tag>
                        )}
                    </Space>
                    {e.title && e.path && (
                        <div>
                            <Text type="secondary" style={{fontSize: 12, wordBreak: "break-all"}}>
                                {e.path}
                            </Text>
                        </div>
                    )}
                    <div>
                        <Text type="secondary" style={{fontSize: 12}}>
                            {formatTime(e.occurredAt)}
                            {e.dwellMs ? ` · ${formatDwell(e.dwellMs, t)}` : ""}
                        </Text>
                    </div>
                </div>
            ),
        };
    });

    return (
        <div>
            {current && (
                <div style={{marginBottom: 10}}>
                    <Space size={6} wrap>
                        <EnvironmentOutlined/>
                        <Text type="secondary" style={{fontSize: 12}}>
                            {t("journey.now_viewing", "در حال مشاهده:")}
                        </Text>
                        <Tooltip title={current.url}>
                            <a href={current.url} target="_blank" rel="noopener noreferrer">
                                <Text strong style={{fontSize: 13}}>
                                    {current.title || current.path || current.url}
                                </Text>
                            </a>
                        </Tooltip>
                    </Space>
                </div>
            )}
            <Space size={12} wrap style={{marginBottom: 10}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    <LoginOutlined/> {t("journey.pages_seen", "صفحات بازدیدشده")}:{" "}
                    {faDigits(data?.distinctPages ?? entries.length)}
                </Text>
                <Text type="secondary" style={{fontSize: 12}}>
                    <ClockCircleOutlined/> {t("journey.total_steps", "گام‌های مسیر")}:{" "}
                    {faDigits(entries.length)}
                </Text>
            </Space>
            <Timeline
                items={items}
                mode="left"
                style={compact ? {maxHeight: 260, overflowY: "auto"} : undefined}
            />
        </div>
    );
};

const faDigits = (v) => String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const formatTime = (iso) => {
    if (!iso) return "";
    try {
        return faDigits(
            new Date(iso).toLocaleTimeString("fa-IR", {hour: "2-digit", minute: "2-digit"})
        );
    } catch (e) {
        return "";
    }
};

/** Dwell is context, not a metric — seconds and minutes are as precise as it needs to be. */
const formatDwell = (ms, t) => {
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${faDigits(seconds)} ${t("journey.seconds", "ثانیه")}`;
    const minutes = Math.round(seconds / 60);
    return `${faDigits(minutes)} ${t("journey.minutes", "دقیقه")}`;
};

export default VisitorJourney;
